<script setup>
import { i18n } from 'boot/i18n';
import {
  HrConfirmBtn,
  HrDismissBtn,
  HrFormDialog,
  HrListSelect,
  HrMultiSelect,
} from 'src/design-system';
import { computed, ref } from 'vue';

/**
 * Picks which of the clinic's records a payer rule is about.
 *
 * ONE DIALOG FOR BOTH LISTS on the funding source editor — the covered services and the place-of-
 * service overrides — because the question is the same one twice: choose a record the clinic already
 * holds, and a row appears for it. What differs is the title and the options, which is a difference
 * in words rather than in shape. `AddApprovedServiceDialog` next door stays its own component for the
 * opposite reason: its add carries a second decision (one service or a shared pool), so its shape is
 * genuinely different rather than differently labelled.
 *
 * A DIALOG RATHER THAN A SELECT ON THE FORM, following the same pattern as the authorization editor:
 * the list ends in an add ROW, and the row opens this. A bare select sitting under the list would
 * read as another field of the funding source rather than as an action on the list above it.
 *
 * THE SELECT FILTERS (`use-input`), because a clinic can carry thirty or more treatment services —
 * the reason the approved-service picker gives for the same choice.
 *
 * WHETHER IT TAKES SEVERAL AT ONCE TURNS ON WHETHER A CHOICE ARRIVES FINISHED, which is why the two
 * lists it serves answer differently. A covered service arrives complete: modifiers are optional and
 * a row carrying none is a valid end state, so picking fifteen at once finishes fifteen things, and a
 * clinic setting a payer up does add that many in one sitting. A place override arrives INCOMPLETE —
 * an override with no code means nothing and is dropped on save — so each one picked creates required
 * follow-up work in a different control. Taking several of those would batch the cheap half of the
 * job and leave the expensive half unbatched, which is how somebody ends up with four half-filled
 * rows and no idea which they had already done. Single also simply costs less for one: the menu
 * closes on the pick instead of waiting for a second choice that is not coming.
 *
 * `ok` CARRIES A LIST EITHER WAY. The prop changes the control, never the payload, so a caller reads
 * one shape — the arrangement `AddApprovedServiceDialog` already uses next door.
 *
 * IT ONLY EVER ADDS. A multi-select FIELD on the form would hold the whole set, so clearing a chip
 * would drop a service and the payer rules set for it with no confirmation. This holds only what is
 * being added now; removal stays an explicit, confirmed action on the row.
 *
 * IT OFFERS ONLY WHAT IS STILL ADDABLE. The caller removes what the payer already holds, because a
 * second rule for one record is not expressible — the entry IS the payer's configuration for it — so
 * offering one would offer a choice that cannot be made. That differs deliberately from the approved-
 * service picker, which MARKS what is used rather than removing it: a funder can legitimately approve
 * one service twice under different settings.
 */
defineOptions({ name: 'AddPayerRuleDialog' });

const props = defineProps({
  /** The dialog's title — what is being added, in the caller's words. */
  title: {
    type: String,
    required: true,
  },
  /** The select's label. */
  label: {
    type: String,
    required: true,
  },
  /** What may still be added, as `{ label, value }`. */
  options: {
    type: Array,
    default: () => [],
  },
  /** Whether several may be taken in one go. See the note above on when that helps and when it does not. */
  multiple: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits({
  /** The chosen records' ids, in the order they were picked. */
  ok: null,
});

const dialog = ref(null);
const selected = ref([]);

const canConfirm = computed(() => selected.value.length > 0);

/** The single select speaks in one value or null; `selected` stays a list so the emit does not vary. */
const singleValue = computed(() => selected.value[0] ?? null);

function onSingleUpdate(value) {
  selected.value = value === null || value === undefined ? [] : [value];
} // onSingleUpdate()

/** Opens empty: this only ever adds, so there is no current selection to seed from. */
function show() {
  selected.value = [];
  dialog.value?.show();
} // show()

function hide() {
  dialog.value?.hide();
} // hide()

function onSubmit() {
  if (!canConfirm.value) {
    return;
  }

  emit('ok', [...selected.value]);
  hide();
} // onSubmit()

defineExpose({ show, hide });
</script>

<template>
  <hr-form-dialog
    ref="dialog"
    size="sm"
    :title="props.title"
    data-e2e="dialog-add-payer-rule"
    @submit="onSubmit"
  >
    <div class="row q-col-gutter-md">
      <hr-multi-select
        v-if="props.multiple"
        v-model="selected"
        class="col-12"
        :options="props.options"
        :label="props.label"
        use-input
        hide-bottom-space
        data-e2e="select-payer-rule-target"
      />

      <hr-list-select
        v-else
        class="col-12"
        :model-value="singleValue"
        :options="props.options"
        :label="props.label"
        use-input
        hide-bottom-space
        data-e2e="select-payer-rule-target"
        @update:model-value="onSingleUpdate"
      />
    </div>

    <template #actions>
      <hr-dismiss-btn
        :label="i18n.t('common.buttonCancel')"
        @click="hide"
      />
      <hr-confirm-btn
        :label="i18n.t('common.buttonAdd')"
        type="submit"
        :disable="!canConfirm"
        data-e2e="btn-confirm-payer-rule"
      />
    </template>
  </hr-form-dialog>
</template>
